import React, {ChangeEvent, useMemo, useState} from 'react';

import {Avatar} from 'src/components/avatar/avatar';
import {searchUsers, searchUsersSuccess} from 'src/openapi/apis/UserControllerApi';
import {User} from 'src/openapi/models/User';
import {useAppDispatch} from 'src/store';

import {Autosuggest, AutosuggestItem, AutosuggestNoResults} from '@pb/kit-component-autosuggest-v2';
import {Group, Item} from '@pb/kit-component-group';

type Props = {
    watchersEmails: string[],
    onSelect: (user: User) => void,
};

const MIN_SUGGESTION_LENGTH = 3;

export const UserAutosuggest = ({watchersEmails, onSelect}: Props) => {
    const dispatch = useAppDispatch();
    const [suggestUsers, setSuggestUsers] = useState<Array<User>>([]);
    const [value, setValue] = useState<string>('');

    const isWatcher = (user: User) => watchersEmails.includes(user.email);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputQuery = event.target.value;
        setValue(inputQuery);

        if (inputQuery.length >= MIN_SUGGESTION_LENGTH) {
            dispatch(searchUsers({searchPhrase: inputQuery})).then(e => {
                if (!e.payload.error && e.type === searchUsersSuccess.type) {
                    const suggests = e.payload.data;
                    setSuggestUsers(suggests.filter(user => !isWatcher(user)));
                }
            });
            return;
        }

        setSuggestUsers([]);
    };

    const onClear = () => {
        setSuggestUsers([]);
        setValue('');
    };

    const onBlur = () => {
        onClear();
    };

    const onItemSelect = (user: User) => {
        onSelect(user);
        onClear();
    };

    const users = useMemo(() => suggestUsers.map(user => (
        <AutosuggestItem
            key={user.email}
            value={user}
            caption={(
                <Group className="pdx-xxs" alignItems="center" gap="s">
                    <Avatar user={user}/>
                    <Item>{user.firstName} {user.lastName}</Item>
                </Group>
            )}
        />
    )), [suggestUsers]);

    return (
        <Autosuggest
            value={value}
            type="ABSOLUTE"
            maxLength={50}
            onChange={onChange}
            onItemSelect={onItemSelect}
            onClear={onClear}
            onBlur={onBlur}>
            {users.length ? users : <AutosuggestNoResults text=""/>}
        </Autosuggest>
    );
};
